import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import { MonthSwitcher } from "../components/MonthSwitcher";
import { StatusBadge } from "../components/StatusBadge";
import { EntryForm, monthBoundsFor } from "../components/EntryForm";
import { defaultReportingMonth, editableRange, isEditableMonth } from "../lib/reportingWindow";
import { parseISO } from "../lib/dates";
import { useI18n, useMonthLabel, useT, useWageCodeLabel } from "../i18n/I18nContext";

export function ReportingPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const t = useT();
  const { locale } = useI18n();
  const monthLabel = useMonthLabel();
  const wageCodeLabel = useWageCodeLabel();
  const [{ year, month }, setMonth] = useState(defaultReportingMonth());
  const range = useMemo(editableRange, []);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reportQuery = useQuery({
    queryKey: ["my-report", year, month],
    queryFn: () => api.getMyReport(year, month),
  });
  const wageCodesQuery = useQuery({
    queryKey: ["wage-codes"],
    queryFn: () => api.listWageCodes(),
  });

  const report = reportQuery.data?.report;
  const wageCodes = wageCodesQuery.data?.wageCodes ?? [];
  const monthIsEditable = isEditableMonth(year, month);
  const reportEditable =
    !!report && monthIsEditable && (report.status === "DRAFT" || report.status === "REJECTED");

  const setNoDeviations = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      api.patchReport(id, { noDeviations: value }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-report"] }),
    onError: (e: Error) => setError(e.message),
  });

  const addEntry = useMutation({
    mutationFn: (data: {
      reportId: string;
      fromDate: string;
      toDate: string;
      wageCodeCode: string;
      hours: number | null;
      note: string | null;
    }) =>
      api.addEntry(data.reportId, {
        fromDate: data.fromDate,
        toDate: data.toDate,
        wageCodeCode: data.wageCodeCode,
        hours: data.hours,
        note: data.note,
      }),
    onSuccess: () => {
      setShowForm(false);
      queryClient.invalidateQueries({ queryKey: ["my-report"] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const deleteEntry = useMutation({
    mutationFn: ({ reportId, entryId }: { reportId: string; entryId: string }) =>
      api.deleteEntry(reportId, entryId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-report"] }),
    onError: (e: Error) => setError(e.message),
  });

  const submit = useMutation({
    mutationFn: (id: string) => api.submitReport(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-report"] }),
    onError: (e: Error) => setError(e.message),
  });

  const recall = useMutation({
    mutationFn: (id: string) => api.recallReport(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-report"] }),
    onError: (e: Error) => setError(e.message),
  });

  const bounds = monthBoundsFor(year, month);

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-portal-primary sm:text-3xl">
            {monthLabel(month)} {year}
          </h1>
          <p className="mt-1 text-sm text-text-secondary">
            {t("reporting.subtitle")}
          </p>
        </div>
        <MonthSwitcher
          year={year}
          month={month}
          onChange={(y, m) => setMonth({ year: y, month: m })}
          allowedRange={range}
        />
      </div>

      {!monthIsEditable && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {t("reporting.locked")}
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}{" "}
          <button onClick={() => setError(null)} className="ml-2 underline">
            {t("common.close")}
          </button>
        </div>
      )}

      {reportQuery.isLoading && (
        <div className="text-text-secondary">{t("app.loading")}</div>
      )}

      {report && (
        <>
          <section className="card p-4 sm:p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-text-muted">
                  {t("reporting.status")}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <StatusBadge status={report.status} />
                  {report.submittedAt && (
                    <span className="text-xs text-text-secondary">
                      {t("reporting.submittedAt", {
                        date: new Date(report.submittedAt).toLocaleString(locale),
                      })}
                    </span>
                  )}
                </div>
                {report.rejectionReason && (
                  <div className="mt-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                    <strong>{t("reporting.rejected")}</strong> {report.rejectionReason}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                {reportEditable && (
                  <>
                    <button
                      className={`${
                        report.noDeviations ? "btn-success" : "btn-secondary"
                      } w-full sm:w-auto`}
                      onClick={() =>
                        setNoDeviations.mutate({
                          id: report.id,
                          value: !report.noDeviations,
                        })
                      }
                      disabled={(report.entries ?? []).length > 0}
                      title={
                        (report.entries ?? []).length > 0
                          ? t("reporting.noDevs.disableHint")
                          : ""
                      }
                    >
                      {report.noDeviations
                        ? t("reporting.noDevs.checked")
                        : t("reporting.noDevs.mark")}
                    </button>
                    <button
                      className="btn-primary w-full sm:w-auto"
                      onClick={() => submit.mutate(report.id)}
                      disabled={
                        submit.isPending ||
                        (!report.noDeviations && (report.entries ?? []).length === 0) ||
                        !user?.employeeId
                      }
                    >
                      {submit.isPending
                        ? t("reporting.submitting")
                        : t("reporting.submit")}
                    </button>
                  </>
                )}
                {report.status === "SUBMITTED" && (
                  <button
                    className="btn-secondary w-full sm:w-auto"
                    onClick={() => recall.mutate(report.id)}
                    disabled={recall.isPending}
                  >
                    {t("reporting.recall")}
                  </button>
                )}
              </div>
            </div>
          </section>

          <section className="card overflow-hidden">
            <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <div>
                <h2 className="text-base font-semibold text-text-primary">
                  {t("reporting.deviations")}
                </h2>
                <p className="text-xs text-text-secondary">
                  {t("reporting.deviationsHelp")}
                </p>
              </div>
              {reportEditable && !showForm && !report.noDeviations && (
                <button
                  className="btn-primary w-full sm:w-auto"
                  onClick={() => setShowForm(true)}
                >
                  {t("reporting.addDeviation")}
                </button>
              )}
            </div>

            {(report.entries ?? []).length === 0 && !showForm && (
              <div className="px-4 py-10 text-center text-sm text-text-secondary sm:px-5">
                {report.noDeviations
                  ? t("reporting.empty.noDeviations")
                  : t("reporting.empty.notAdded")}
              </div>
            )}

            {(report.entries ?? []).length > 0 && (
              <ul className="divide-y divide-border">
                {(report.entries ?? []).map((e) => {
                  const wage = wageCodes.find((w) => w.code === e.wageCodeCode);
                  const f = parseISO(e.fromDate);
                  const tDate = parseISO(e.toDate);
                  const sameDay = e.fromDate === e.toDate;
                  return (
                    <li
                      key={e.id}
                      className="flex items-start gap-3 px-4 py-3 sm:items-center sm:gap-4 sm:px-5"
                    >
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-portal-surface-tint text-portal-primary">
                        <span className="text-xs font-bold">{e.wageCodeCode}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-text-primary">
                          {wageCodeLabel(e.wageCodeCode, wage?.description ?? e.wageCodeCode)}
                          {e.hours ? <span> — {e.hours} h</span> : null}
                        </div>
                        <div className="text-xs text-text-secondary">
                          {sameDay
                            ? f.toLocaleDateString(locale, {
                                weekday: "short",
                                day: "numeric",
                                month: "short",
                              })
                            : `${f.toLocaleDateString(locale, {
                                day: "numeric",
                                month: "short",
                              })} – ${tDate.toLocaleDateString(locale, {
                                day: "numeric",
                                month: "short",
                              })}`}
                          {e.note && (
                            <span className="ml-2 block italic text-text-muted sm:ml-2 sm:inline">
                              {e.note}
                            </span>
                          )}
                        </div>
                      </div>
                      {reportEditable && (
                        <button
                          className="btn-ghost shrink-0 px-2"
                          onClick={() =>
                            deleteEntry.mutate({ reportId: report.id, entryId: e.id })
                          }
                          aria-label={t("reporting.delete")}
                        >
                          ✕
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {showForm && reportEditable && (
              <div className="border-t border-border bg-surface-alt px-4 py-4 sm:px-5 sm:py-5">
                <EntryForm
                  wageCodes={wageCodes}
                  monthBounds={bounds}
                  busy={addEntry.isPending}
                  onCancel={() => setShowForm(false)}
                  onSubmit={async (data) => {
                    await addEntry.mutateAsync({ reportId: report.id, ...data });
                  }}
                />
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
